import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiSettings, FiDatabase, FiZap, FiBell, FiUsers, FiShield, FiSave, FiX, FiRefreshCw, FiCheck, FiLoader } = FiIcons;

// Load automation rules from localStorage
const loadAutomationRules = () => {
  try {
    const saved = localStorage.getItem('fresh-board-automation-rules');
    if (saved) return JSON.parse(saved);
  } catch (e) {}
  return [
    { id: 1, name: 'Stock Level Alerts', trigger: 'Inventory < Min Threshold', action: 'Send WhatsApp Alert', enabled: true },
    { id: 2, name: 'Auto Invoice Generation', trigger: 'Delivery Confirmed', action: 'Generate Invoice in Pastel', enabled: true },
    { id: 3, name: 'Customer Follow-up', trigger: '7 Days After Delivery', action: 'Send Loyalty Points Update', enabled: true },
    { id: 4, name: 'Debtor Reminders', trigger: 'Payment Overdue 30 Days', action: 'Send Payment Reminder', enabled: false }
  ];
};

const Settings = () => {
  const [activeTab, setActiveTab] = useState('integrations');
  const [configuringIntegration, setConfiguringIntegration] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [automationRules, setAutomationRules] = useState(loadAutomationRules);
  const [editingRule, setEditingRule] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', trigger: '', action: '' });

  // Save automation rules to localStorage when they change
  React.useEffect(() => {
    localStorage.setItem('fresh-board-automation-rules', JSON.stringify(automationRules));
  }, [automationRules]);

  const toggleRule = (ruleId) => {
    setAutomationRules(prev => prev.map(rule =>
      rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    ));
    const rule = automationRules.find(r => r.id === ruleId);
    toast.success(`${rule.name} ${rule.enabled ? 'disabled' : 'enabled'}`);
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setEditForm({ name: rule.name, trigger: rule.trigger, action: rule.action });
  };

  const saveRuleEdit = () => {
    setAutomationRules(prev => prev.map(rule =>
      rule.id === editingRule.id ? { ...rule, ...editForm } : rule
    ));
    toast.success(`${editForm.name} updated successfully`);
    setEditingRule(null);
  };

  // Integration configuration fields
  const integrationConfigs = {
    'Pastel v19 ERP': {
      fields: [
        { name: 'serverUrl', label: 'Server URL', type: 'text', placeholder: 'https://erp.company.com' },
        { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Enter your API key' },
        { name: 'companyCode', label: 'Company Code', type: 'text', placeholder: 'COMP001' },
        { name: 'syncInterval', label: 'Sync Interval (minutes)', type: 'number', placeholder: '15' }
      ]
    },
    'WhatsApp Business API': {
      fields: [
        { name: 'phoneNumberId', label: 'Phone Number ID', type: 'text', placeholder: 'Enter phone number ID' },
        { name: 'accessToken', label: 'Access Token', type: 'password', placeholder: 'Enter access token' },
        { name: 'webhookUrl', label: 'Webhook URL', type: 'text', placeholder: 'https://api.company.com/webhook' },
        { name: 'verifyToken', label: 'Verify Token', type: 'password', placeholder: 'Enter verify token' }
      ]
    },
    'Butler Automations': {
      fields: [
        { name: 'apiEndpoint', label: 'API Endpoint', type: 'text', placeholder: 'https://butler.api.com' },
        { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Enter API key' },
        { name: 'workspaceId', label: 'Workspace ID', type: 'text', placeholder: 'ws_123456' }
      ]
    },
    'Google Sheets': {
      fields: [
        { name: 'spreadsheetId', label: 'Spreadsheet ID', type: 'text', placeholder: 'Enter spreadsheet ID' },
        { name: 'serviceAccountJson', label: 'Service Account JSON', type: 'textarea', placeholder: 'Paste service account credentials' },
        { name: 'sheetName', label: 'Sheet Name', type: 'text', placeholder: 'Sheet1' }
      ]
    },
    'Payment Gateway': {
      fields: [
        { name: 'merchantId', label: 'Merchant ID', type: 'text', placeholder: 'Enter merchant ID' },
        { name: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Enter API key' },
        { name: 'secretKey', label: 'Secret Key', type: 'password', placeholder: 'Enter secret key' },
        { name: 'environment', label: 'Environment', type: 'select', options: ['Sandbox', 'Production'] }
      ]
    }
  };

  const testConnection = () => {
    setTestingConnection(true);
    setConnectionStatus(null);

    // Simulate connection test
    setTimeout(() => {
      const success = Math.random() > 0.3; // 70% success rate for demo
      setTestingConnection(false);
      setConnectionStatus(success ? 'success' : 'error');

      if (success) {
        toast.success('Connection successful!');
      } else {
        toast.error('Connection failed. Please check credentials.');
      }
    }, 2000);
  };

  const saveConfiguration = () => {
    toast.success(`${configuringIntegration.name} configuration saved`);
    setConfiguringIntegration(null);
    setConnectionStatus(null);
  };

  const integrationStatus = [
    { name: 'Pastel v19 ERP', status: 'connected', lastSync: '2 mins ago', type: 'database' },
    { name: 'WhatsApp Business API', status: 'connected', lastSync: '1 min ago', type: 'messaging' },
    { name: 'Butler Automations', status: 'connected', lastSync: '30 secs ago', type: 'automation' },
    { name: 'Google Sheets', status: 'connected', lastSync: '5 mins ago', type: 'data' },
    { name: 'Payment Gateway', status: 'disconnected', lastSync: 'Never', type: 'payment' }
  ];

  const getStatusBadgeStyle = (status) => {
    if (status === 'connected') {
      return 'bg-lime-500/10 text-lime-400 border-lime-500/30';
    } else if (status === 'disconnected') {
      return 'bg-red-500/10 text-red-400 border-red-500/30';
    }
    return 'bg-[#243129] text-[#6b8772] border-[#243129]';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'database': return FiDatabase;
      case 'messaging': return FiBell;
      case 'automation': return FiZap;
      case 'data': return FiSettings;
      case 'payment': return FiShield;
      default: return FiSettings;
    }
  };

  const tabs = [
    { id: 'integrations', label: 'System Integrations', icon: FiDatabase },
    { id: 'automation', label: 'Automation Rules', icon: FiZap },
    { id: 'notifications', label: 'Notifications', icon: FiBell },
    { id: 'users', label: 'User Management', icon: FiUsers }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#e8f5e9]">
            System <span className="text-[#84cc16]">Settings</span>
          </h1>
          <p className="text-[#6b8772] font-mono text-sm mt-1">Configure integrations and automation workflows</p>
        </div>
        <button className="bg-gradient-to-r from-lime-600 to-lime-500 text-white px-4 py-2 rounded-lg hover:from-lime-500 hover:to-lime-400 transition-all flex items-center space-x-2 shadow-lg shadow-lime-500/20">
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          <span>Save Changes</span>
        </button>
      </div>

      {/* System Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#84cc16]/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">System Health</p>
              <p className="text-2xl font-bold text-lime-400 mt-1">Excellent</p>
            </div>
            <div className="bg-lime-500/10 rounded-lg p-3 border border-lime-500/20">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-lime-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#84cc16]/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">Active Integrations</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">4/5</p>
            </div>
            <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
              <SafeIcon icon={FiDatabase} className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#84cc16]/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">Automation Rules</p>
              <p className="text-2xl font-bold text-purple-400 mt-1">3/4</p>
            </div>
            <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20">
              <SafeIcon icon={FiZap} className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Settings Panel */}
      <div className="bg-[#111916] rounded-xl border border-[#243129]">
        <div className="border-b border-[#243129]">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#84cc16] text-[#84cc16]'
                    : 'border-transparent text-[#6b8772] hover:text-[#e8f5e9]'
                }`}
              >
                <SafeIcon icon={tab.icon} className="w-4 h-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'integrations' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">Integration Status</h3>
                <div className="space-y-4">
                  {integrationStatus.map((integration, index) => (
                    <motion.div
                      key={integration.name}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 bg-[#0a0f0d] border border-[#1c2822] rounded-lg hover:border-[#84cc16]/30 hover:bg-[#0d1210] transition-all"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="bg-[#1c2822] rounded-lg p-3">
                          <SafeIcon icon={getTypeIcon(integration.type)} className="w-5 h-5 text-[#6b8772]" />
                        </div>
                        <div>
                          <h4 className="font-medium text-[#e8f5e9]">{integration.name}</h4>
                          <p className="text-[#6b8772] font-mono text-sm">Last sync: {integration.lastSync}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadgeStyle(integration.status)}`}>
                          {integration.status.toUpperCase()}
                        </span>
                        <button
                          onClick={() => setConfiguringIntegration(integration)}
                          className="text-[#84cc16] hover:text-lime-300 text-sm font-medium transition-colors"
                        >
                          Configure
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'automation' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">Automation Rules</h3>
                <div className="space-y-4">
                  {automationRules.map((rule, index) => (
                    <motion.div
                      key={rule.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 bg-[#0a0f0d] border border-[#1c2822] rounded-lg hover:border-[#84cc16]/30 hover:bg-[#0d1210] transition-all"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          rule.enabled ? 'bg-lime-500/10 border border-lime-500/20' : 'bg-[#1c2822]'
                        }`}>
                          <SafeIcon
                            icon={FiZap}
                            className={`w-5 h-5 ${rule.enabled ? 'text-lime-400' : 'text-[#6b8772]'}`}
                          />
                        </div>
                        <div>
                          <h4 className="font-medium text-[#e8f5e9]">{rule.name}</h4>
                          <p className="text-[#6b8772] font-mono text-sm">Trigger: {rule.trigger}</p>
                          <p className="text-[#6b8772] font-mono text-sm">Action: {rule.action}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={rule.enabled}
                            onChange={() => toggleRule(rule.id)}
                          />
                          <div className="w-11 h-6 bg-[#243129] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lime-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-[#6b8772] after:border-[#243129] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-lime-600 peer-checked:after:bg-white"></div>
                        </label>
                        <button
                          onClick={() => openEditRule(rule)}
                          className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                        >
                          Edit
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">Notification Preferences</h3>
                <div className="space-y-4">
                  {[
                    { name: 'Stock Level Alerts', description: 'Get notified when inventory is low', enabled: true },
                    { name: 'Order Updates', description: 'Receive updates on order status changes', enabled: true },
                    { name: 'Payment Reminders', description: 'Alerts for overdue payments', enabled: false },
                    { name: 'System Maintenance', description: 'Notifications about system updates', enabled: true }
                  ].map((notification, index) => (
                    <motion.div
                      key={notification.name}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 bg-[#0a0f0d] border border-[#1c2822] rounded-lg hover:border-[#84cc16]/30 hover:bg-[#0d1210] transition-all"
                    >
                      <div>
                        <h4 className="font-medium text-[#e8f5e9]">{notification.name}</h4>
                        <p className="text-[#6b8772] font-mono text-sm">{notification.description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          defaultChecked={notification.enabled}
                        />
                        <div className="w-11 h-6 bg-[#243129] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-lime-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-[#6b8772] after:border-[#243129] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-lime-600 peer-checked:after:bg-white"></div>
                      </label>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">User Management</h3>
                <div className="text-center py-12">
                  <SafeIcon icon={FiUsers} className="w-12 h-12 text-[#6b8772] mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-[#e8f5e9] mb-2">User Management</h3>
                  <p className="text-[#6b8772]">Manage user accounts, roles, and permissions</p>
                  <button className="mt-4 bg-gradient-to-r from-lime-600 to-lime-500 text-white px-6 py-2 rounded-lg hover:from-lime-500 hover:to-lime-400 transition-all shadow-lg shadow-lime-500/20">
                    Manage Users
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Automation Rule Modal */}
      <AnimatePresence>
        {editingRule && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setEditingRule(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-[#111916] border border-[#243129] rounded-xl w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-5 border-b border-[#243129] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/10 border border-purple-500/20 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiZap} className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-[#e8f5e9]">Edit Automation Rule</h3>
                    <p className="text-xs text-[#6b8772]">Modify rule parameters</p>
                  </div>
                </div>
                <button
                  onClick={() => setEditingRule(null)}
                  className="p-2 hover:bg-[#243129] rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5 text-[#6b8772]" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-mono text-[#6b8772] mb-1">Rule Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono text-[#6b8772] mb-1">Trigger Condition</label>
                  <input
                    type="text"
                    value={editForm.trigger}
                    onChange={(e) => setEditForm(prev => ({ ...prev, trigger: e.target.value }))}
                    className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono text-[#6b8772] mb-1">Action</label>
                  <input
                    type="text"
                    value={editForm.action}
                    onChange={(e) => setEditForm(prev => ({ ...prev, action: e.target.value }))}
                    className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500"
                  />
                </div>
                <div className="p-3 bg-[#0a0f0d] border border-[#1c2822] rounded-lg">
                  <p className="text-xs text-[#6b8772]">
                    Status: <span className={editingRule.enabled ? 'text-lime-400' : 'text-red-400'}>
                      {editingRule.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-5 border-t border-[#243129] flex gap-3">
                <button
                  onClick={() => setEditingRule(null)}
                  className="flex-1 bg-[#1c2822] border border-[#243129] text-[#e8f5e9] py-2 rounded-lg hover:bg-[#243129] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveRuleEdit}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] py-2 rounded-lg font-medium hover:from-lime-400 hover:to-lime-500 transition-all"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Integration Configuration Modal */}
      <AnimatePresence>
        {configuringIntegration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setConfiguringIntegration(null); setConnectionStatus(null); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-[#111916] border border-[#243129] rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-5 border-b border-[#243129] flex items-center justify-between sticky top-0 bg-[#111916]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1c2822] rounded-lg flex items-center justify-center">
                    <SafeIcon icon={getTypeIcon(configuringIntegration.type)} className="w-5 h-5 text-lime-400" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-[#e8f5e9]">{configuringIntegration.name}</h3>
                    <p className="text-xs text-[#6b8772]">Configure integration settings</p>
                  </div>
                </div>
                <button
                  onClick={() => { setConfiguringIntegration(null); setConnectionStatus(null); }}
                  className="p-2 hover:bg-[#243129] rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5 text-[#6b8772]" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-5 space-y-4">
                {/* Connection Status */}
                <div className={`p-3 rounded-lg border flex items-center justify-between ${
                  configuringIntegration.status === 'connected'
                    ? 'bg-lime-500/10 border-lime-500/30'
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      configuringIntegration.status === 'connected' ? 'bg-lime-400 animate-pulse' : 'bg-red-400'
                    }`} />
                    <span className={`text-sm font-medium ${
                      configuringIntegration.status === 'connected' ? 'text-lime-400' : 'text-red-400'
                    }`}>
                      {configuringIntegration.status === 'connected' ? 'Connected' : 'Disconnected'}
                    </span>
                  </div>
                  <span className="text-xs text-[#6b8772]">Last sync: {configuringIntegration.lastSync}</span>
                </div>

                {/* Configuration Fields */}
                {integrationConfigs[configuringIntegration.name]?.fields.map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-mono text-[#6b8772] mb-1">{field.label}</label>
                    {field.type === 'textarea' ? (
                      <textarea
                        rows={4}
                        placeholder={field.placeholder}
                        className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500 resize-none"
                      />
                    ) : field.type === 'select' ? (
                      <select className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500">
                        <option value="">Select {field.label}</option>
                        {field.options?.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        placeholder={field.placeholder}
                        className="w-full bg-[#0a0f0d] border border-[#243129] rounded-lg px-4 py-2 text-[#e8f5e9] font-mono text-sm focus:outline-none focus:border-lime-500"
                      />
                    )}
                  </div>
                ))}

                {/* Test Connection Result */}
                {connectionStatus && (
                  <div className={`p-3 rounded-lg border flex items-center gap-2 ${
                    connectionStatus === 'success'
                      ? 'bg-lime-500/10 border-lime-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    <SafeIcon
                      icon={connectionStatus === 'success' ? FiCheck : FiX}
                      className={`w-5 h-5 ${connectionStatus === 'success' ? 'text-lime-400' : 'text-red-400'}`}
                    />
                    <span className={`text-sm font-medium ${
                      connectionStatus === 'success' ? 'text-lime-400' : 'text-red-400'
                    }`}>
                      {connectionStatus === 'success' ? 'Connection test successful!' : 'Connection test failed. Please verify credentials.'}
                    </span>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-5 border-t border-[#243129] flex gap-3 sticky bottom-0 bg-[#111916]">
                <button
                  onClick={testConnection}
                  disabled={testingConnection}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#1c2822] border border-[#243129] text-[#e8f5e9] py-2 rounded-lg hover:bg-[#243129] transition-colors disabled:opacity-50"
                >
                  <SafeIcon
                    icon={testingConnection ? FiLoader : FiRefreshCw}
                    className={`w-4 h-4 ${testingConnection ? 'animate-spin' : ''}`}
                  />
                  <span>{testingConnection ? 'Testing...' : 'Test Connection'}</span>
                </button>
                <button
                  onClick={saveConfiguration}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] py-2 rounded-lg font-medium hover:from-lime-400 hover:to-lime-500 transition-all"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  <span>Save Configuration</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Settings;
